import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { CheckCircle, AlertTriangle, Clock, TimerOff, Inbox, ClockFading } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../../../../components/ui/card";
import Avatar from "../../../../../../../components/shared/Avatar";
import { useFilters } from "../../../../../../../utils/hooks/useFilters";
import useApi from "../../../../../../../utils/api";
import { formatTimeSheetParams } from "../../../../../../TimeSheet/api/helpers";
import { renderSettingsResourceLabel } from "../../../../../../../utils/helpers/settings";
import { renderUserName } from "../../../../../../../utils/helpers/user";
import { cn } from "../../../../../../../lib/utils";

const stats = [
    [
        { key: "early", base: "tw-bg-sky-50 tw-text-sky-600 tw-border-sky-100", active: "tw-bg-sky-100 tw-border-sky-200" },
        { key: "on-time", base: "tw-bg-green-50 tw-text-green-600 tw-border-green-100", active: "tw-bg-green-100 tw-border-green-200" },
    ],
    [
        { key: "late", base: "tw-bg-yellow-50 tw-text-yellow-600 tw-border-yellow-100", active: "tw-bg-yellow-100 tw-border-yellow-200" },
        { key: "absent", base: "tw-bg-red-50 tw-text-red-600 tw-border-red-100", active: "tw-bg-red-100 tw-border-red-200" },
        { key: "not-scheduled", base: "tw-bg-purple-50 tw-text-purple-600 tw-border-purple-100", active: "tw-bg-purple-100 tw-border-purple-200" },
    ]
];

const gridCols: Record<number, string> = { 2: "tw-grid-cols-2", 3: "tw-grid-cols-3" };

function EmployeeAttendance() {
    const { t } = useTranslation();
    const [_filters] = useFilters();
    const [selected, setSelected] = useState<any>();
    const { authGet } = useApi();

    const filters = useMemo(() => {
        return {
            ...formatTimeSheetParams({
                filters: _filters?.filters || [],
                from:
                    _filters?.mode === "month"
                        ? moment(_filters?.from).startOf("week").format("YYYY-MM-DD")
                        : _filters?.from,
                to:
                    _filters?.mode === "month"
                        ? moment(_filters?.to).endOf("week").format("YYYY-MM-DD")
                        : _filters?.to
            }),
            sort: _filters?.sort,
            mode: _filters?.mode,
        };
    }, [_filters]);

    const query = useQuery({
        queryKey: ["/schedules/v2/employee-attendance", filters],
        queryFn: ({ queryKey }: any) => {
            const [url, params] = queryKey;
            return authGet(url, { params });
        }
    });

    const allAttendance = useMemo(() => {
        if (!query.data) return [];

        const mapWithType = (items: any[], type: string) =>
            (items || [])
                .filter((item: any) => item && item.id)
                .map((item: any) => ({
                    ...item,
                    id: item.id + type,
                    type,
                }));

        return [
            ...mapWithType(query.data?.early?.shifts, "early"),
            ...mapWithType(query.data?.["on-time"]?.shifts, "on-time"),
            // ...mapWithType(query.data?.late?.shifts, "late"),
            ...mapWithType(query.data?.absent?.shifts, "absent"),
        ];
    }, [query.data]);

    const attendance = useMemo(() => {
        if (!selected) return allAttendance;
        return Array.from(
            new Map(allAttendance.map(item => [item.id, item])).values()
        ).filter((item: any) => item.type === selected);
    }, [allAttendance, selected]);

    const statusConfig: any = useMemo(() => ({
        early: {
            label: t("early"),
            icon: ClockFading,
            className: "bg-sky-50 text-sky-600",
        },
        "on-time": {
            label: t("on-time"),
            icon: CheckCircle,
            className: "bg-green-50 text-green-600",
        },
        late: {
            label: t("late"),
            icon: AlertTriangle,
            className: "bg-yellow-50 text-yellow-600",
        },
        absent: {
            label: t("absent"),
            icon: Clock,
            className: "bg-red-50 text-red-600",
        },
        "not-scheduled": {
            label: t("not-scheduled"),
            icon: TimerOff,
            className: "bg-purple-50 text-purple-600",
        }
    }), [t]);

    return (
        <Card className="tw-w-full tw-h-full tw-rounded-xl tw-border-gray-200">
            <CardHeader className="tw-pb-4">
                <CardTitle className="tw-text-sm tw-font-semibold tw-text-gray-700">
                    {t("employee-attendance")}
                </CardTitle>

                <div className="tw-space-y-2 tw-mt-3">
                    {stats.map((group, index) => (
                        <div key={index} className={cn("tw-grid tw-gap-2", gridCols[group.length])}>
                            {group.map((stat) => (
                                <div
                                    key={stat.key}
                                    onClick={() => setSelected((prev: any) => prev === stat.key ? undefined : stat.key)}
                                    className={cn(
                                        "tw-rounded-lg tw-border tw-p-3 tw-cursor-pointer tw-transition-colors",
                                        selected === stat.key ? stat.active : stat.base
                                    )}
                                >
                                    <p className="tw-text-xs tw-font-medium tw-truncate">{t(stat.key)}</p>
                                    <p className="tw-text-lg tw-font-bold tw-mt-0.5">
                                        {query.data?.[stat.key]?.total || 0}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </CardHeader>

            <CardContent className="tw-space-y-3 tw-max-h-[8.75rem] tw-overflow-auto">
                {attendance.length === 0 ? (
                    <div className="tw-flex tw-items-center tw-justify-center tw-flex-col tw-py-3 tw-border tw-border-dashed tw-border-gray-200 tw-rounded-lg tw-text-gray-400 tw-text-sm">
                        <Inbox className="tw-mb-1 tw-opacity-30" />
                        <span>{t("no-data")}</span>
                    </div>
                ) : attendance.map((item: any) => {
                    const config = statusConfig[item.type];
                    const Icon = config.icon;

                    return (
                        <div key={item.id} className="tw-flex tw-items-center tw-justify-between">
                            <div className="tw-flex tw-items-center tw-gap-2.5">
                                <Avatar user={item.user} size="sm" />
                                <div>
                                    <p className="tw-text-xs tw-font-medium tw-text-gray-800">
                                        {renderUserName(item.user)}
                                    </p>
                                    <p className="tw-text-[11px] tw-text-gray-400">
                                        {renderSettingsResourceLabel(item.location)}
                                    </p>
                                </div>
                            </div>

                            <span className={cn("tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-md tw-text-[11px] tw-font-medium", config.className)}>
                                <Icon className="tw-h-3 tw-w-3" />
                                {config.label}
                                {item.lateMinutes && ` (${item.lateMinutes}m)`}
                            </span>
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
}

export default EmployeeAttendance;
